import { FETCH_SESSION_CODE, CHECK_SESSION_STATE } from './constants';
import { SessionStateHandler } from "./SessionStateHandler";


const { TOKEN_EXPIRE_SAFETY_NET } = CHECK_SESSION_STATE;
const { DELAY } = FETCH_SESSION_CODE;



export class OAuthHandler {

    _authenticating = false
    _pendingRequests = [];

    constructor(options = {}) {
        const {
			clientSecret = '',
            redirectUri = '',
            clientId = '',
            scope = '',
            service
        } = options;
		const { pathname } = new URL(redirectUri);

        this._clientSecret = clientSecret;
        this._redirectUri = redirectUri;
        this._securityRoute = pathname;
        this._clientId = clientId;
        this._service = service;
        this._options = options;
        this._scope = scope;
        this._sessionStateHandler = this._initSessionStateHandler();

        setInterval(() => {
            this.getCredentials().then(()=>{});
        }, 5000);

        if(!this._routeContainsSecutiryCode()) {
			return this
		};

		this._resolveCredentials()
		return this
    }

    get clientCredentials() {
		return this._clientCredentials || this._options.getClientCredentials() || {};
	}

    get location() {
		const {
			getLocation = () => window.location
		} = this._options;

		return getLocation() || {};
	}

    set navigate(path) {
		const {
			navigate = path => window.location.href = path
		} = this._options;

		return navigate(path);
	}

    set navigateExternally(path) {
		if(globalThis.location) {
			return window.location.href = path
		};

		return this.navigate = path;
	}

	getCredentials() {
		return new Promise((resolve, reject) => {
			this._pendingRequests.push({ resolve, reject });
			this._resolveCredentials();
		});
	}

    syncReactState({
        removeClientCredentials,
        setClientCredentials,
        getClientCredentials,
        destroySession
    }) {
		this._options = {
            ...this._options,
			removeClientCredentials,
			setClientCredentials,
			getClientCredentials,
			destroySession
		};
	}

    getOAuthClientCredentials() {
		return {
            client_secret: this._clientSecret,
            redirect_uri: this._redirectUri,
            client_id: this._clientId,
            scope:  this._scope
        };
	}

	logout() {
		const credentials = this.clientCredentials;
		const url = this._service.getUrl('oauth2_logout', {
			state: credentials['session_state'],
			idToken: credentials['id_token'],
			redirect: window.location.href
		});

		this._clientCredentials = {};
		this._options.destroySession();
		return setTimeout(() => this.navigateExternally = url, DELAY.REDIRECT);
	}

    _initSessionStateHandler() {
		return new SessionStateHandler({
			sessionStateHandlerCB: state => this._handleSessionState(state),
			clientCredentials: this.getOAuthClientCredentials(),
			service: this._service
		});
	}

    _routeContainsSecutiryCode({ pathname = '', search  = '' } = this.location) {
		return !!(pathname.indexOf(this._securityRoute) > -1 && (search && (search.indexOf('code=') > -1 || search.indexOf('error=') > -1)));
	}

    _setClientCredentials(sessionCredential) {
		const prevCredentials = this.clientCredentials;

		this._clientCredentials = {
			...prevCredentials,
			...sessionCredential,
			'login_time': Date.now(),
			'scope': ''
		};

		return this._options.setClientCredentials(this._clientCredentials);
	}

    _removeClientCredentials() {
		this._clientCredentials = {};
        return this._options.removeClientCredentials();
    }

    _redirectToPreLoginRoute() {
		const { login_route: path = '' } = this.clientCredentials;
		const [pathname, search = ''] = path.split('?');
		const isAuthRoute = this._routeContainsSecutiryCode({ pathname, search });

		if (path && !isAuthRoute) {
			this.navigate = path; //TODO ??? change to nextjs router
			return this;
		};

		this.navigate = '/'; //TODO ??? change to nextjs router
		return this;
	}

    _handleSessionState(state) {
		if (state) {
			this._flushPendingRequests();
			return this;
		};

		this._removeClientCredentials();
		this._authenticating = false;
		this._resolveCredentials();
		return this;
	}

    _flushPendingRequests() {
		this._authenticating = false;
        this._pendingRequests.map(({ resolve }) => resolve(this.clientCredentials));
        this._pendingRequests = [];
		return this;
	}

    _resolveCredentials() {
		if (this._authenticating) {
			return;
		};

		this._authenticating = true;

		if (this._routeContainsSecutiryCode()) {
			return this._handleOAuthCode()
				.then(code => this._grantAccessToken(code))
				.then(() => this._redirectToPreLoginRoute())
				.then(() => this._checkSessionState());
		};

		const credentials = this.clientCredentials;

		if (!credentials['access_token']) {
			return this._fetchSessionCode();
		};

		const passedTime = ((Date.now() - credentials['login_time']) / 1000);

		if (passedTime >= credentials['expires_in'] - TOKEN_EXPIRE_SAFETY_NET) {
			return this._revalidateAccessToken()
				.then(() => this._checkSessionState());
		};

		return this._checkSessionState();
	}

    async _grantAccessToken(code) {
		return this._service.grantAccessToken(code, this.getOAuthClientCredentials())
			.then(({ body }) => {
				return this._setClientCredentials(body);
			})
			.catch(error => this._authDanceErrorHandler(error));
	}

	async _revalidateAccessToken() {
		return this._service.revalidateAccessToken(this.clientCredentials['refresh_token'], this.getOAuthClientCredentials())
			.then(({ body }) => {
				return this._setClientCredentials(body)
			})
			.catch(error => this._revalidateTokenErrorHandler(error));
	}

	_handleOAuthCode() {
		const { search = '' } = this.location;
		const [ ,code ] = search.match(/[\?|\&]code\=([^\&]+)/i) || [];

		if(code) {
			return Promise.resolve(code);
		} else {
			return Promise.reject(new Error('Api response does not match the expected pattern!'));
		};
	}

    _fetchSessionCode() {
		const url = this._service.getOauth2AuthorizationUrl(this.getOAuthClientCredentials());

		//Only to ensure scheduled redirects are finished before send to api login route
		//Safety measure
		setTimeout(() => {
			const { pathname = '', search = '', hash = '' } = this.location;

			this._setClientCredentials({ 'login_route': `${pathname}${search}${hash}` });

			//Ensure local storage write before redirect
			//Safety measure
			setTimeout(() => this.navigateExternally = url, DELAY.REDIRECT);
		}, DELAY.SAVE_CREDENTIALS);
		return this;
	}

    _checkSessionState() {
		try {
			this._sessionStateHandler.checkState(this.clientCredentials);
		} catch (error) {
			console.error(error);
		};
		return this;
	}

    _authDanceErrorHandler(error) {
		/* TODO error ? what to do next? */
		console.error(error);
		this._removeClientCredentials();
		throw Error(error);
	}

	_revalidateTokenErrorHandler(error) {
		console.error(error);
		return this.logout();
	}

    close() {
        this._authenticating = false
        this._pendingRequests = [];

        if(this._sessionStateHandler) {
            this._sessionStateHandler.close();
        };

        delete this._clientCredentials;
        delete this._securityRoute;
        delete this._clientSecret;
        delete this._redirectUri;
        delete this._location;
        delete this._clientId;
        delete this._options;
        delete this._scope;
        return this;
    }
};